import React, { FC, useCallback, useEffect, useState } from 'react';
import { ExtendableImage } from '@design-system/extendable-image';
import { HeadingBlock } from '@design-system/heading-block';
import { Modal } from '@design-system/modal';
import PromoForm from '@husky-x/gatsby-theme-husky/src/components/PromoForm';
import { isBrowser } from '@phx-husky/browser-helpers';
import { getLocalStorageData, setLocalStorageData } from '@phx-husky/local-storage';
import { formPopupSchema } from '@root/components/FormPopup/FormPopup.schema';
import useJoiValidation from '@root/hooks/useJoiValidation';
import { gatsbyImageMapper } from '@theme-husky/mappers/image';

import { IFormPopupProps } from './models';

import './FormPopup.scss';

const FormPopup: FC<IFormPopupProps> = ({
  title,
  image,
  formId,
  closeBtn,
  tyText,
  tyImage,
  isModalShowedOnlyOnce,
  onCloseLocalStorageValue,
  submitErrorMessage,
  modalAppearingDelay,
}) => {
  const { data }: { data: IFormPopupProps } = useJoiValidation(formPopupSchema, 'FormPopup', {
    title,
    image,
    formId,
    closeBtn,
    tyText,
    tyImage,
    isModalShowedOnlyOnce,
    onCloseLocalStorageValue,
    submitErrorMessage,
    modalAppearingDelay,
  });

  const shouldShowInitially = data.isModalShowedOnlyOnce
    ? !getLocalStorageData(data?.onCloseLocalStorageValue)
    : true;

  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

  const handleModalClose = useCallback(() => {
    document.body.style.overflow = 'auto';

    setIsModalVisible(false);

    if (data.isModalShowedOnlyOnce) {
      setLocalStorageData(data?.onCloseLocalStorageValue, true);
    }
  }, [data.isModalShowedOnlyOnce, data.onCloseLocalStorageValue]);

  useEffect(() => {
    if (!shouldShowInitially) return undefined;

    const delay = (Number(data.modalAppearingDelay) || 0) * 1000;

    const timeout = setTimeout(() => {
      setIsModalVisible(true);
    }, delay);

    return () => clearTimeout(timeout);
  }, [shouldShowInitially, data.modalAppearingDelay]);

  useEffect(() => {
    if (isModalVisible) {
      document.body.style.overflow = 'hidden';
    }
  }, [isModalVisible]);

  return isModalVisible ? (
    <Modal
      isShowing={isBrowser()}
      close={handleModalClose}
      closeModal={handleModalClose}
      className="form-popup__modal"
      closeAriaLabel={data.closeBtn}
      closeIconSize="large"
      showHeader
      showCloseButton
      topElements={
        <HeadingBlock
          className="form-popup__heading"
          headline={data.title}
          headlineTag="h2"
          headlineSize="s"
          align="center"
        />
      }
    >
      {data.formId ? (
        <div className="form-popup__wrapper">
          <ExtendableImage
            element={gatsbyImageMapper({
              alt: data.title,
              image: data.image,
              objectFit: 'cover',
            })}
          />
          <PromoForm
            pageBlockUI={{ disableGutters: true, mode: 'inverse', element: 'div' }}
            form={{
              url: process.env.GATSBY_PROMO_PLATFORM_URL || '',
              formId,
              submitErrorMessage: data.submitErrorMessage,
              showHeading: true,
            }}
            thankYou={{
              closeButtonLabel: data.closeBtn,
              heading: { headline: data.title },
              image: { props: { src: data.tyImage.url } },
            }}
            uid="promo-form_cc"
          />
        </div>
      ) : null}
    </Modal>
  ) : null;
};

export default FormPopup;
